import { useCallback, useEffect, useState } from 'react';

import moment from 'moment';
import 'moment/locale/pt-br';

import * as VerificadoresAPI from '../../../../api/verificadores';

import { retornaData, retornaDataHora } from '../../../../utils/retornaDataFormatada';

export default function useDadosEmpresaModule({ token, dados, setDados, municipios, navigate, setFlashMessage, onlyNumbers }) {
    const [preview, setPreview] = useState([]);
    const [optionsMunicipios, setOptionsMunicipios] = useState([]);

    const normalizarNumeros = useCallback(
        (valor) => {
            if (typeof onlyNumbers === 'function') {
                return onlyNumbers(String(valor ?? ''));
            }

            return String(valor ?? '').replace(/\D/g, '');
        },
        [onlyNumbers]
    );

    const consultaMunicipio = useCallback(async (ufId, uf, descricao) => {
        if (!ufId && !uf && !descricao) {
            setOptionsMunicipios([]);
            return [];
        }

        try {
            const resultado = await VerificadoresAPI.municipio(ufId, uf, descricao);

            const lista = Array.isArray(resultado) ? resultado : [];

            setOptionsMunicipios(lista);

            return lista;
        } catch (error) {
            console.error('Erro ao consultar municípios:', error);

            setOptionsMunicipios([]);

            return [];
        }
    }, []);

    const consultaReceita = useCallback(
        async (cnpj) => {
            try {
                const result = await VerificadoresAPI.cnpj(cnpj);

                if (result?.message && result?.type) {
                    setFlashMessage?.(result.message, result.type);

                    return null;
                }

                setDados((prev) => ({
                    ...prev,

                    razaoSocial: result?.nome ?? prev.razaoSocial,
                    nomeFantasia: result?.fantasia ?? prev.nomeFantasia,

                    abertura: result?.abertura ? retornaData(result.abertura) : prev.abertura,

                    ultima_atualizacao: result?.ultima_atualizacao
                        ? retornaDataHora(result.ultima_atualizacao)
                        : prev.ultima_atualizacao,

                    email: result?.email ?? prev.email,
                    cep: result?.cep ?? prev.cep,

                    uf: result?.uf ?? prev.uf,
                    ufId: result?.ufId ?? prev.ufId,
                    municipioId: result?.municipioId ?? prev.municipioId,

                    bairro: result?.bairro ?? prev.bairro,
                    logradouro: result?.logradouro ?? prev.logradouro,

                    numero: result?.numero ?? prev.numero,
                    complemento: result?.complemento ?? prev.complemento,

                    telefone: result?.telefone ?? prev.telefone,

                    tipo: result?.tipo ?? prev.tipo,
                }));

                if (result?.ufId) {
                    await consultaMunicipio(result.ufId);
                }

                return result;
            } catch (error) {
                console.error('Erro ao consultar CNPJ:', error);

                setFlashMessage?.(
                    error?.response?.data?.message || 'Erro ao consultar o CNPJ.',
                    error?.response?.data?.type || 'error'
                );

                return null;
            }
        },
        [consultaMunicipio, setDados, setFlashMessage]
    );

    const consultaCep = useCallback(
        async (cep) => {
            try {
                const result = await VerificadoresAPI.cep(cep);

                if (result?.type === 'error') {
                    setFlashMessage?.(result.message, result.type);

                    return null;
                }

                setDados((prev) => ({
                    ...prev,

                    ufId: result?.ufId ?? prev.ufId,
                    municipioId: result?.municipioId ?? prev.municipioId,

                    bairro: result?.neighborhood ?? prev.bairro,

                    logradouro: result?.street ?? prev.logradouro,
                }));

                if (result?.ufId) {
                    await consultaMunicipio(result.ufId);
                }

                return result;
            } catch (error) {
                console.error('Erro ao consultar CEP:', error);

                setFlashMessage?.(
                    error?.response?.data?.message || 'Erro ao consultar o CEP.',
                    error?.response?.data?.type || 'error'
                );

                return null;
            }
        },
        [consultaMunicipio, setDados, setFlashMessage]
    );

    const onFileChange = useCallback(
        (event) => {
            const arquivos = Array.from(event.target.files ?? []);

            setPreview(arquivos);

            setDados((prev) => ({
                ...prev,
                logomarca: arquivos,
            }));
        },
        [setDados]
    );

    const handleChange = useCallback(
        (event) => {
            const { name, value, checked, type, options, selectedIndex } = event.target;

            if (name === 'cnpj' || name === 'cep') {
                setDados((prev) => ({
                    ...prev,
                    [name]: normalizarNumeros(value),
                }));

                return;
            }

            if (name === 'tipo') {
                const textoSelecionado = options?.[selectedIndex]?.text ?? value;

                setDados((prev) => ({
                    ...prev,
                    tipo: textoSelecionado,

                    ...(value === 'FILIAL'
                        ? {
                              possuiFilial: false,
                          }
                        : {}),
                }));

                return;
            }

            if (name === 'ramoAtividade') {
                const textoSelecionado = options?.[selectedIndex]?.text ?? value;

                setDados((prev) => ({
                    ...prev,
                    ramoAtividade: textoSelecionado,
                }));

                return;
            }

            if (name === 'ufId') {
                setDados((prev) => ({
                    ...prev,
                    ufId: value,
                    municipioId: '',
                }));

                consultaMunicipio(value);

                return;
            }

            if (name === 'municipioId') {
                setDados((prev) => ({
                    ...prev,
                    municipioId: value,
                }));

                return;
            }

            setDados((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        },
        [consultaMunicipio, normalizarNumeros, setDados]
    );

    const handleOnBlur = useCallback(
        async (event) => {
            const { name, value } = event.target;

            if (name === 'cnpj') {
                const cnpj = normalizarNumeros(value);

                setDados((prev) => ({
                    ...prev,
                    cnpj,
                }));

                if (cnpj.length !== 11 && cnpj.length !== 14) {
                    setFlashMessage?.('O campo não é CPF nem CNPJ?', 'error');

                    return;
                }

                if (cnpj.length === 14) {
                    await consultaReceita(cnpj);
                }

                return;
            }

            if (name === 'cep') {
                const cep = normalizarNumeros(value);

                setDados((prev) => ({
                    ...prev,
                    cep,
                }));

                if (cep.length === 8) {
                    await consultaCep(cep);
                } else if (cep.length > 0) {
                    setFlashMessage?.('CEP inválido!', 'warning');
                }
            }
        },
        [consultaCep, consultaReceita, normalizarNumeros, setDados, setFlashMessage]
    );

    const handleOnFocus = useCallback(
        (event) => {
            if (event.target.name === 'ufId') {
                consultaMunicipio(event.target.value || dados?.ufId);
            }
        },
        [consultaMunicipio, dados?.ufId]
    );

    useEffect(() => {
        if (!token) {
            navigate('/Auth', {
                replace: true,
            });
        }
    }, [token, navigate]);

    useEffect(() => {
        setOptionsMunicipios(Array.isArray(municipios) ? municipios : []);
    }, [municipios]);

    return {
        state: {
            dados,
            preview,
            optionsMunicipios,
        },

        defaults: {
            abertura: dados?.abertura ? retornaData(dados.abertura) : retornaData(moment()),

            ultimaAtualizacao: dados?.ultima_atualizacao ? retornaDataHora(dados.ultima_atualizacao) : retornaDataHora(moment()),
        },

        actions: {
            onFileChange,
            handleChange,
            handleOnBlur,
            handleOnFocus,
            consultaMunicipio,
            consultaReceita,
            consultaCep,
        },
    };
}
